import { CartOrderSummary, CartProductTable, EmptyCart } from "@/components";
import { Button } from "@/components/ui/button";
import { useCartManagement } from "@/hooks/useCartManagement";
import { LoaderCircle, ShoppingCart, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useRef } from "react";
import { useAppSelector } from "@/store/hooks";

const Cart = () => {
  const {
    cartItems,
    cartTotal,
    cartItemCount,
    isLoading,
    isAuthenticated,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    isUpdatingCart,
    isRemovingFromCart,
    isClearingCart,
  } = useCartManagement();
  const { cartItems: cartItemsRedux } = useAppSelector((state) => state.app);

  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const tableRef = useRef<{ applyPendingUpdates: () => void }>(null);

  const handleUpdateQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
    } else {
      updateCartItemQuantity(itemId, newQuantity);
    }
  };

  const handleUpdateCart = () => {
    if (tableRef.current) {
      tableRef.current.applyPendingUpdates();
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-[calc(100vh-600px)]">
        <LoaderCircle className="w-6 h-6 animate-spin mr-4"></LoaderCircle>
        Fetching cart items. This won't take long!
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10 px-4">
      {cartItems?.length > 0 || cartItemsRedux?.length > 0 ? (
        <>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <ShoppingCart className="h-9 w-9 text-black" />
              <h1 className="text-3xl font-bold">Your Cart</h1>
            </div>

          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="border rounded-lg overflow-hidden">
                <CartProductTable
                  ref={tableRef}
                  cartItems={cartItems || cartItemsRedux}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemoveItem={removeFromCart}
                  isUpdating={isUpdatingCart}
                  isRemoving={isRemovingFromCart}
                  isAuthenticated={isAuthenticated}
                  onPendingChangesChange={setHasPendingChanges}
                />
              </div>

              <div className="flex justify-between mt-4">
                <div className="flex gap-2">
                  <Button
                    onClick={clearCart}
                    variant="outline"
                    className="text-sm hover:bg-black hover:text-white"
                    disabled={isClearingCart}
                  >
                    {isClearingCart ? "Clearing..." : "Clear Cart"}
                  </Button>
                  {isAuthenticated && (hasPendingChanges || isUpdatingCart) && (
                    <Button
                      onClick={handleUpdateCart}
                      disabled={isUpdatingCart}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {isUpdatingCart  ? 'Updating...' : 'Update Cart'}
                    </Button>
                  )}
                </div>
                <Button
                  variant="outline"
                  className="text-sm hover:bg-black hover:text-white"
                  asChild
                >
                  <Link to="/products">Continue Shopping</Link>
                </Button>
              </div>
            </div>

            <div className="md:col-span-1 md:max-w-2xl max-w-[300px]">
              <CartOrderSummary
                cartItems={cartItems}
                cartTotal={cartTotal}
                cartItemCount={cartItemCount}
              />
            </div>
          </div>
        </>
      ) : (
        <EmptyCart />
      )}
    </div>
  );
};

export default Cart;
